import { BI_CATEGORY_BEHAVIOR_IN_ROOM, BI_EVENT_DEVICE_INCOMPATIBLE } from '@/constants/bi-sdk'
import { useBISdkContext } from '@/context/BISdkContext'
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext'
import { useConfigsContext } from '@/context/ConfigsContext'
import useInitialTrailerViewMedia from '@/hooks/useInitialTrailerViewMedia'
import usePollTrailerViewBlendVisionVodStatus from '@/hooks/usePollTrailerViewBlendVisionVodStatus'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useInterval } from 'usehooks-ts'

export const MEDIA_TYPE = {
  VIDEO: 'video',
  IMAGE: 'image',
} as const
export const CAROUSEL_DURATION_MS = 2500
export const VOD_STATUS_FAILED_LIST = [
  // the status of vod reference: https://developers.blendvision.com/zh/docs/api/bv-one/public-cms-service-list-vo-ds#responses
  'VOD_STATUS_FAILED',
  'VOD_STATUS_CANCELLED',
  'VOD_STATUS_DELETED',
]
export const isVODStatusFailed = (status: string) => VOD_STATUS_FAILED_LIST.includes(status)

export const useTrailerViewMedia = () => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const { roomData } = useCheckEnterRoomContext()
  const [playKey, setPlayKey] = useState(0)
  const { sendBIReport } = useBISdkContext()
  const { accountType } = useConfigsContext()
  const {
    videos: originVideos,
    images,
    isLoading,
    isVideoLoading,
  } = useInitialTrailerViewMedia(roomData.id)

  // Send BI report when user enters trailer view
  useEffect(() => {
    sendBIReport({
      ev: BI_EVENT_DEVICE_INCOMPATIBLE,
      category: BI_CATEGORY_BEHAVIOR_IN_ROOM,
      a1: accountType,
      a2: window.location.href,
      a3: roomData.title,
      a4: roomData.id,
    })
  }, [accountType, roomData.id, roomData.title, sendBIReport])

  // Get lastet videos status after polling
  const polledVideos = usePollTrailerViewBlendVisionVodStatus(originVideos)
  const videos = polledVideos ?? originVideos

  // Merge videos and images with placeholder
  const mediaList = useMemo(() => {
    const typedVideos = videos.map((v) => ({
      ...v,
      type: MEDIA_TYPE.VIDEO,
    }))
    const typedImages = images.map((i) => ({
      ...i,
      type: MEDIA_TYPE.IMAGE,
    }))
    let list = [...typedVideos, ...typedImages]
    if (!isLoading && list.length === 0 && roomData.image) {
      list = [
        {
          duration: CAROUSEL_DURATION_MS,
          type: MEDIA_TYPE.IMAGE,
          url: roomData.image,
          isPlaceholder: false,
          key: roomData.image,
        },
      ]
    }
    return list
  }, [videos, images, roomData.image, isLoading])

  const currentMedia = mediaList[currentIndex] || null
  const isEmpty = !(mediaList.length > 0 && currentMedia !== null)

  const goNext = useCallback(() => {
    const total = mediaList.length
    const currentMedia = mediaList[currentIndex] || null
    setCurrentIndex((prev) => {
      const nextIndex = (prev + 1) % total
      if (nextIndex === prev && currentMedia?.type === MEDIA_TYPE.VIDEO) {
        setPlayKey((key) => key + 1)
      }
      return (prev + 1) % total
    })
  }, [currentIndex, mediaList])

  const handleVideoEnded = () => {
    goNext()
  }

  const validVideos = videos.filter((v) => v.isConverted || v.url) // isConverted is true then use iframe or fallback use url
  const hasMedia = videos.length > 0 || images.length > 0
  const isWaitingForVideos = isVideoLoading && validVideos.length <= 0
  const canAutoPlayImage =
    currentMedia?.type === MEDIA_TYPE.IMAGE && hasMedia && !isWaitingForVideos
  const delay =
    currentMedia?.type === MEDIA_TYPE.IMAGE && 'duration' in currentMedia
      ? currentMedia.duration
      : CAROUSEL_DURATION_MS
  useInterval(
    () => {
      /* Don't autoplay image if videos are still loading or not ready
       * Automatically update currentIndex to trigger carousel every few seconds
       */
      if (canAutoPlayImage) {
        goNext()
      }
    },
    canAutoPlayImage ? delay : null,
  )

  return {
    mediaList,
    currentIndex,
    setCurrentIndex,
    currentMedia,
    handleVideoEnded,
    playKey,
    goNext,
    isLoading,
    isEmpty,
  }
}
